'use client';

import { getSocketConfig } from '@/lib/socket-config';
import { useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { create } from 'zustand';

// Define socket event types for type safety
export enum SocketEventType {
  // Notification events
  NOTIFICATION = 'notification',
  NOTIFICATIONS_COUNT = 'notifications:count',
  NOTIFICATIONS_UPDATED = 'notifications:updated',

  // Shift events
  SHIFT_SWAP_REQUESTED = 'shift.swap_requested',
  SHIFT_SWAP_UPDATED = 'shift.swap_updated',

  // Test events
  TEST_EVENT = 'test-event'
}

// Message event tracking to prevent double-processing
interface EventTracker {
  messageIds: Map<string, number>; // Map of message IDs to timestamps
  cleanupInterval: NodeJS.Timeout | undefined;
  lastCleanup: number; // Last cleanup timestamp
}

// Define socket store state
interface SocketState {
  socket: Socket | null;
  isConnected: boolean;
  error: string | null;
  userId: string;
  userName: string;
  /**
   * Authenticated user's role (e.g. 'ADMIN', 'INSTALLER', 'USER'), seeded from
   * the session by UnifiedSocketProvider. Sent in the handshake as advisory
   * metadata only — the socket server authenticates the session cookie and
   * derives the authoritative role itself, so this value is not trusted for
   * room membership. `undefined` for unauthenticated sessions.
   */
  role: string | undefined;
  connectionAttempts: number;
  maxReconnectAttempts: number;
  reconnectDelay: number;
  eventTracker: EventTracker;
  reconnectTimer: NodeJS.Timeout | undefined;

  // Socket actions
  initializeSocket: () => void;
  reconnect: () => void;
  emit: <T>(event: string, data?: T) => boolean;
  on: <T>(event: string, callback: (data: T) => void) => () => void;
  off: <T>(event: string, callback: (data: T) => void) => void;
  cleanup: () => void;
}

// Adjust these constants for better resilience
const MAX_RECONNECT_ATTEMPTS = 20; // Increase from 15 to 20
const RECONNECT_BASE_DELAY = 1000; // 1 second base delay
const MESSAGE_DEDUPLICATION_WINDOW = 30000; // Increase from 20 seconds to 30 seconds
const MAX_BACKOFF_DELAY = 60000; // Maximum backoff delay (1 minute)

// Create socket store
export const useSocketStore = create<SocketState>((set, get) => ({
  socket: null,
  isConnected: false,
  error: null,
  userId: typeof window !== 'undefined' ? `user-${Math.random().toString(36).substring(2, 9)}` : '',
  userName: typeof window !== 'undefined' ? `User ${Math.floor(Math.random() * 1000)}` : '',
  role: undefined,
  connectionAttempts: 0,
  maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
  reconnectDelay: RECONNECT_BASE_DELAY,
  eventTracker: {
    messageIds: new Map(),
    cleanupInterval: undefined,
    lastCleanup: Date.now()
  },
  reconnectTimer: undefined,

  initializeSocket: () => {
    // Skip initialization on server
    if (typeof window === 'undefined') return;

    // Clean up any existing socket
    const { socket, reconnectTimer, eventTracker } = get();
    if (socket) {
      socket.disconnect();
    }

    // Clear any pending reconnect timer
    if (reconnectTimer !== null && reconnectTimer !== undefined) {
      clearTimeout(reconnectTimer);
    }

    // Set up event tracking cleanup
    if (!eventTracker.cleanupInterval) {
      const interval = setInterval(() => {
        const now = Date.now();
        const { messageIds } = get().eventTracker;
        let cleanupCount = 0;

        // Only run cleanup if at least 30 seconds have passed or if we have too many cached messages
        if (now - get().eventTracker.lastCleanup > 30000 || messageIds.size > 100) {
          // Remove message IDs older than the deduplication window
          messageIds.forEach((timestamp, id) => {
            if (now - timestamp > MESSAGE_DEDUPLICATION_WINDOW) {
              messageIds.delete(id);
              cleanupCount++;
            }
          });

          // Update last cleanup timestamp
          set(state => ({
            eventTracker: {
              ...state.eventTracker,
              lastCleanup: now
            }
          }));

          if (cleanupCount > 0) {
            console.log(`Cleaned up ${cleanupCount} stale message IDs`);
          }
        }
      }, 30000); // Check for cleanup every 30 seconds

      set(state => ({
        eventTracker: {
          ...state.eventTracker,
          cleanupInterval: interval
        }
      }));
    }

    // Get socket configuration using the new unified method
    const { url, path, options } = getSocketConfig(false); // Don't skip auth by default

    // The handshake authenticates server-side from the HttpOnly NextAuth
    // session cookie sent via `withCredentials` — there is no JS-readable
    // token to gate on here. This provider is only mounted for authenticated
    // users (see UnifiedSocketProvider / providers.tsx), so anonymous visitors
    // still never reach this code and never open a connection.
    try {
      // Create socket instance with the correct URL and path
      const socketInstance = io(url, {
        ...options,
        path,
        reconnectionAttempts: get().maxReconnectAttempts,
        reconnectionDelay: get().reconnectDelay,
        reconnectionDelayMax: MAX_BACKOFF_DELAY,
        timeout: 20000, // Increase from 15000 to 20000 for slow connections
        auth: {
          ...options.auth,
          // Advisory identity only — used client-side for the
          // `notifications:subscribe` emit and logging. The socket server does
          // NOT trust these for room membership: it derives the user id + role
          // from the verified NextAuth session cookie sent on the handshake
          // (see scripts/build-server.js io.use middleware). Room joins (incl.
          // the `admins` room) are decided from that server-verified role, so a
          // client cannot forge `metadata.role` to receive admin broadcasts.
          userId: get().userId,
          userName: get().userName,
          metadata: {
            role: get().role
          }
        }
      });

      // Set up event handlers
      socketInstance.on('connect', () => {
        console.log('Socket connected successfully', {
          id: socketInstance.id,
          userId: get().userId
        });

        // Subscribe to notifications
        socketInstance.emit('notifications:subscribe', get().userId);

        set({
          socket: socketInstance,
          isConnected: true,
          error: null,
          connectionAttempts: 0
        });
      });

      socketInstance.on('connect_error', (error) => {
        console.error('Socket connection error:', {
          error: error.message,
          userId: get().userId,
          userName: get().userName,
          url,
          path,
          withCredentials: options.withCredentials,
          attempt: get().connectionAttempts + 1,
          timestamp: new Date().toISOString() // Add timestamp for debugging
        });

        // Authentication rides the session cookie, so always retry transient
        // handshake failures with exponential backoff.
        const newAttempts = get().connectionAttempts + 1;

        set({
          isConnected: false,
          error: error.message,
          connectionAttempts: newAttempts
        });

        if (newAttempts < get().maxReconnectAttempts) {
          const jitter = Math.random() * 1000; // Add randomness to prevent thundering herd
          const delay = Math.min(
            get().reconnectDelay * Math.pow(1.5, newAttempts) + jitter,
            MAX_BACKOFF_DELAY
          );

          // Clear any existing timer
          if (get().reconnectTimer !== undefined) {
            clearTimeout(get().reconnectTimer);
          }

          // Set new timer
          const timer = setTimeout(() => {
            console.log('Attempting to reconnect...', {
              attempt: get().connectionAttempts,
              userId: get().userId,
              delay
            });
            get().reconnect();
          }, delay);

          set({ reconnectTimer: timer });
        } else {
          console.error('Max reconnection attempts reached. Please refresh the page.');
        }
      });

      socketInstance.on('disconnect', (reason) => {
        console.log('Socket disconnected:', {
          reason,
          userId: get().userId
        });

        set({
          isConnected: false,
          error: `Disconnected: ${reason}`
        });

        // Try to reconnect for transient drops (auth rides the session cookie).
        if (reason !== 'io client disconnect') {
          // Handle reconnection for transient reasons
          if (['transport close', 'ping timeout', 'transport error'].includes(reason)) {
            const newAttempts = get().connectionAttempts + 1;

            if (newAttempts < get().maxReconnectAttempts) {
              // Clear any existing timer
              if (get().reconnectTimer !== undefined) {
                clearTimeout(get().reconnectTimer);
              }

              const delay = Math.min(
                get().reconnectDelay * Math.pow(1.5, newAttempts),
                30000 // Max 30 seconds for disconnect reconnects
              );

              const timer = setTimeout(() => {
                console.log('Attempting to reconnect after disconnect...', {
                  reason,
                  userId: get().userId,
                  attempt: newAttempts,
                  delay
                });
                get().reconnect();
              }, delay);

              set({
                reconnectTimer: timer,
                connectionAttempts: newAttempts
              });
            }
          }
        }
      });

      // Set up enhanced event handlers for capacity management events
      [SocketEventType.SHIFT_SWAP_REQUESTED, SocketEventType.SHIFT_SWAP_UPDATED].forEach(event => {
        socketInstance.on(event, (data) => {
          if (data && typeof data === 'object' && 'id' in data) {
            const messageKey = `${event}:${data.id}`;
            const { messageIds } = get().eventTracker;
            const now = Date.now();

            // Check if we've seen this message before within the deduplication window
            if (!messageIds.has(messageKey)) {
              messageIds.set(messageKey, now);
              console.log(`Received ${event}:`, {
                id: data.id,
                userId: get().userId,
                timestamp: new Date(now).toISOString()
              });
            } else {
              const originalTimestamp = messageIds.get(messageKey)!;
              const age = now - originalTimestamp;

              // Update the timestamp for frequently seen messages to extend their lifetime
              messageIds.set(messageKey, now);

              console.log(`Ignoring duplicate event ${event}:`, {
                id: data.id,
                userId: get().userId,
                age: `${age}ms`,
                originalTimestamp: new Date(originalTimestamp).toISOString(),
                currentTimestamp: new Date(now).toISOString()
              });
            }
          }
        });
      });

      // Set up notification event handlers
      socketInstance.on(SocketEventType.NOTIFICATION, (data) => {
        console.log('Received notification:', {
          data,
          userId: get().userId,
          userName: get().userName
        });
      });

      socketInstance.on(SocketEventType.NOTIFICATIONS_COUNT, (count) => {
        console.log('Received notifications count:', {
          count,
          userId: get().userId,
          userName: get().userName
        });
      });

      // Set the socket instance
      set({
        socket: socketInstance,
        error: null
      });
    } catch (error) {
      console.error('Failed to initialize socket:', error);
      set({
        error: `Failed to initialize socket: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isConnected: false
      });
    }
  },

  reconnect: () => {
    const { socket, reconnectTimer } = get();

    // Clean up existing socket connection and timers
    if (socket) {
      socket.disconnect();
    }

    if (reconnectTimer !== undefined) {
      clearTimeout(reconnectTimer);
    }

    set({
      reconnectTimer: undefined,
      socket: null // Explicitly set socket to null to prevent stale references
    });

    // Start a new connection after a short delay to avoid immediate reconnection
    setTimeout(() => {
      get().initializeSocket();
    }, 100);
  },

  emit: <T>(event: string, data?: T) => {
    const { socket, isConnected } = get();
    if (socket && isConnected) {
      try {
        // For events with an ID, track them to prevent duplicates
        if (data && typeof data === 'object' && 'id' in data) {
          const messageKey = `${event}:${(data as any).id}`;
          const { messageIds } = get().eventTracker;

          // Only emit if we haven't recently sent this exact message
          if (!messageIds.has(messageKey)) {
            messageIds.set(messageKey, Date.now());
            socket.emit(event, data);
            return true; // Successfully emitted
          } else {
            console.log(`Prevented duplicate emit for ${event}:`, {
              id: (data as any).id,
              userId: get().userId,
              timestamp: new Date().toISOString()
            });
            return false; // Prevented duplicate
          }
        } else {
          // For events without IDs, just emit them
          socket.emit(event, data);
          return true; // Successfully emitted
        }
      } catch (error) {
        console.error(`Error emitting socket event ${event}:`, error);
        return false; // Failed to emit
      }
    } else {
      console.warn(`Cannot emit event ${event}: socket not connected`);
      return false; // Not connected
    }
  },

  on: <T>(event: string, callback: (data: T) => void) => {
    const { socket } = get();

    // Create a wrapper that protects against duplicate events
    const wrappedCallback = (data: T) => {
      try {
        if (data && typeof data === 'object' && 'id' in data) {
          const messageKey = `${event}:${(data as any).id}`;
          const { messageIds } = get().eventTracker;
          const now = Date.now();

          // Only process if we haven't seen this exact message recently
          if (!messageIds.has(messageKey)) {
            messageIds.set(messageKey, now);
            callback(data);
          } else {
            const originalTimestamp = messageIds.get(messageKey)!;
            const age = now - originalTimestamp;

            // Update the timestamp to keep tracking this message
            messageIds.set(messageKey, now);

            console.log(`Ignored duplicate event ${event}:`, {
              id: (data as any).id,
              age: `${age}ms`,
              timestamp: new Date(now).toISOString()
            });
          }
        } else {
          // For data without IDs, just process normally
          callback(data);
        }
      } catch (error) {
        console.error(`Error processing socket event ${event}:`, error);
      }
    };

    if (socket) {
      socket.on(event, wrappedCallback);
    }

    return () => {
      const { socket } = get();
      if (socket) {
        socket.off(event, wrappedCallback);
      }
    };
  },

  off: <T>(event: string, callback: (data: T) => void) => {
    const { socket } = get();
    if (socket) {
      socket.off(event, callback);
    }
  },

  cleanup: () => {
    const { socket, reconnectTimer, eventTracker } = get();

    // Disconnect socket
    if (socket) {
      try {
        socket.disconnect();
      } catch (error) {
        console.error('Error disconnecting socket:', error);
      }
    }

    // Clear reconnect timer
    if (reconnectTimer !== undefined) {
      clearTimeout(reconnectTimer);
    }

    // Clear event cleanup interval
    if (eventTracker.cleanupInterval !== undefined) {
      clearInterval(eventTracker.cleanupInterval);
    }

    set({
      socket: null,
      isConnected: false,
      reconnectTimer: undefined,
      eventTracker: {
        messageIds: new Map(),
        cleanupInterval: undefined,
        lastCleanup: Date.now()
      }
    });
  }
}));

// General socket hook
export function useSocket() {
  const {
    socket,
    isConnected,
    error,
    userId,
    userName,
    reconnect,
    emit,
    on,
    off
  } = useSocketStore();

  return {
    socket,
    isConnected,
    error,
    userId,
    userName,
    reconnect,
    emit,
    on,
    off
  };
}

// Notification socket hook
export function useNotificationSocket() {
  const { isConnected, emit, on } = useSocketStore();

  const getUnreadCount = useCallback(() => {
    emit(SocketEventType.NOTIFICATIONS_COUNT);
  }, [emit]);

  const markAsRead = useCallback((notificationId: string) => {
    emit(SocketEventType.NOTIFICATIONS_UPDATED, notificationId);
  }, [emit]);

  const onNotification = useCallback(<T>(callback: (data: T) => void) => {
    return on<T>(SocketEventType.NOTIFICATION, callback);
  }, [on]);

  const onUnreadCountUpdate = useCallback(<T>(callback: (count: T) => void) => {
    return on<T>(SocketEventType.NOTIFICATIONS_COUNT, callback);
  }, [on]);

  return {
    isConnected,
    getUnreadCount,
    markAsRead,
    onNotification,
    onUnreadCountUpdate
  };
}


// Socket initialization is handled by UnifiedSocketProvider (only mounted for authenticated users).
// No auto-init on import — this prevents connection attempts for anonymous visitors.
